import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);
ScrollTrigger.config({ ignoreMobileResize: true });

export default class FooterLogo extends HTMLElement {
  private rafId: number | null = null;
  private svgEl: SVGSVGElement | null = null;
  private scaleGroup: SVGGElement | null = null;
  private outerOffset: SVGFEOffsetElement | null = null;
  private innerOffset: SVGFEOffsetElement | null = null;
  private outerBlur: SVGFEGaussianBlurElement | null = null;
  private innerBlur: SVGFEGaussianBlurElement | null = null;

  outerOffsetX: number = 0;
  outerOffsetY: number = 0;
  innerOffsetX: number = 0;
  innerOffsetY: number = 0;
  outerBlurStdDeviation: number = 0;
  innerBlurStdDeviation: number = 0;

  private scrollTl: gsap.core.Timeline | null = null;

  private onUpdateBound = this.onUpdate.bind(this);

  connectedCallback(): void {
    this.svgEl = this.querySelector('svg');
    this.scaleGroup = this.querySelector('[data-footer-logo-ref="scale-group"]');
    this.outerOffset = this.querySelector('[data-footer-logo-ref="outer-offset"]');
    this.innerOffset = this.querySelector('[data-footer-logo-ref="inner-offset"]');
    this.outerBlur = this.querySelector('[data-footer-logo-ref="outer-blur"]');
    this.innerBlur = this.querySelector('[data-footer-logo-ref="inner-blur"]');

    if (this.outerOffset) {
      this.outerOffsetX = parseFloat(this.outerOffset.getAttribute('dx') || '0');
      this.outerOffsetY = parseFloat(this.outerOffset.getAttribute('dy') || '0');
    }
    if (this.innerOffset) {
      this.innerOffsetX = parseFloat(this.innerOffset.getAttribute('dx') || '0');
      this.innerOffsetY = parseFloat(this.innerOffset.getAttribute('dy') || '0');
    }

    if (this.outerBlur) {
      this.outerBlurStdDeviation = parseFloat(this.outerBlur.getAttribute('stdDeviation') || '0');
    }
    if (this.innerBlur) {
      this.innerBlurStdDeviation = parseFloat(this.innerBlur.getAttribute('stdDeviation') || '0');
    }

    if (!this.svgEl || !this.scaleGroup || !this.outerOffset || !this.innerOffset || !this.outerBlur || !this.innerBlur)
      return;

    this.addEventListeners();

    this.scrollTl = gsap.timeline({ paused: true });

    const startScale = 0.99;
    const endScale = 1;
    const scaleDuration = 1;
    const scaleStartTime = 0;

    const startOffsetMultiplier = 0;
    const startBlurMultiplier = 0;
    const endOffsetMultiplier = 1;
    const endBlurMultiplier = 1;

    const offsetStartTime = 0;
    const blurStartTime = 0;
    const offsetDuration = 2.1;
    const blurDuration = 2.1;
    const blurEase = 'power2.out';
    const offsetEase = 'power2.out';

    const viewBox = this.svgEl.viewBox.baseVal;
    const svgOrigin = `${viewBox.x + viewBox.width / 2} ${viewBox.y + viewBox.height}`;

    this.scrollTl.fromTo(
      this.scaleGroup,
      { scale: startScale, svgOrigin },
      { scale: endScale, duration: scaleDuration, ease: 'none' },
      scaleStartTime,
    );
    this.scrollTl.fromTo(
      this.outerOffset,
      { attr: { dx: this.outerOffsetX * startOffsetMultiplier, dy: this.outerOffsetY * startOffsetMultiplier } },
      {
        attr: { dx: this.outerOffsetX * endOffsetMultiplier, dy: this.outerOffsetY * endOffsetMultiplier },
        duration: offsetDuration,
        ease: offsetEase,
      },
      offsetStartTime,
    );
    this.scrollTl.fromTo(
      this.outerBlur,
      { attr: { stdDeviation: this.outerBlurStdDeviation * startBlurMultiplier } },
      {
        attr: { stdDeviation: this.outerBlurStdDeviation * endBlurMultiplier },
        duration: blurDuration,
        ease: blurEase,
      },
      blurStartTime,
    );
    this.scrollTl.fromTo(
      this.innerBlur,
      { attr: { stdDeviation: this.innerBlurStdDeviation * startBlurMultiplier } },
      {
        attr: { stdDeviation: this.innerBlurStdDeviation * endBlurMultiplier },
        duration: blurDuration,
        ease: blurEase,
      },
      blurStartTime,
    );
    this.scrollTl.fromTo(
      this.innerOffset,
      { attr: { dx: this.innerOffsetX * startOffsetMultiplier, dy: this.innerOffsetY * startOffsetMultiplier } },
      {
        attr: { dx: this.innerOffsetX * endOffsetMultiplier, dy: this.innerOffsetY * endOffsetMultiplier },
        duration: offsetDuration,
        ease: offsetEase,
      },
      offsetStartTime,
    );

    ScrollTrigger.create({
      trigger: this,
      start: 'bottom bottom',
      onEnter: () => this.scrollTl?.restart(),
      onEnterBack: () => this.scrollTl?.restart(),
    });

    ScrollTrigger.create({
      trigger: this,
      start: 'top bottom',
      onLeaveBack: () => this.scrollTl?.pause(0),
    });
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
  }

  addEventListeners(): void {}

  removeEventListeners(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private onUpdate(): void {
    this.rafId = requestAnimationFrame(this.onUpdateBound);
  }
}

customElements.define('footer-logo', FooterLogo);
